import { Grid, Typography } from "@mui/material";
import { isFulfilled, nanoid } from "@reduxjs/toolkit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { delay } from "../../../app/common";
import { useAppDispatch } from "../../../app/hooks";
import { ACTION_MENU_DEFAULT_STATE } from "../../../common/components/functional/actionMenu/constants";
import { FilterSearchField } from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import filterFalsyParameters from "../../../common/helpers/filterFalsyParameters";
import isEmpty from "../../../common/helpers/isEmpty";
import { ActionMenuState } from "../../../common/types/commonTypes";
import useMessage from "../../../hooks/useMessage";
import {
  editEmpSalary,
  setEmpAttrSalary,
} from "../../employment/employmentApi";
import { addMassAccumulationDelta } from "../../employment/employmentSlice";
import {
  fetchMassUpdateState,
  fetchSalaries,
  setMassUpdateState,
} from "../settingsActions";
import {
  SalaryIncreaseStatus,
  SalaryTableRow,
  ShiftSalaryRow,
} from "../types/common";
import { Kta, MassUpdateRowClarity, MassUpdateStatusRowId, ShiftSalaryData } from "../types/queries";
import {
  BULK_SALARY_INCREASE_TARGETS, MAX_TO_LOAD,
  SHIFT_SALARY_PAGE_SIZE,
} from "./constants";
import BulkSalaryIncreaseFilters from "./filters/BulkSalaryIncreaseFilters";
import BulkSalaryIncreaseFiltersBar from "./filters/BulkSalaryIncreaseFiltersBar";
import {
  formatEmploymentSalaryToCommand,
  formatFilterDefaultValues,
  formatKtaLaskentaToCommand,
  formatKtaToCommand,
  formatSalaryIncreaseValues,
  formatShiftSalaryToCommand,
  formatTesTablesShiftSalaryToCommand,
  formatToQuery,
  isEmploymentSalaryTableRow,
  isGroupedEmploymentSalaryRow,
  isKtaRow,
  queryToId,
  queryToShortId,
  resolveMassUpdateRowId,
  uiRowEmpToId,
} from "./helpers";
import BulkSalaryIncreaseProgressBar from "./table/BulkSalaryIncreaseProgressBar";
import BulkSalaryIncreaseRowComponents from "./table/BulkSalaryIncreaseRowComponents";
import BulkSalaryIncreaseTable from "./table/BulkSalaryIncreaseTable";
import BulksSalaryIncreaseHeader from "./table/BulksSalaryIncreaseHeader";
import {
  BulkSalaryIncreaseFilterValues,
  BulkSalaryIncreaseProcessingState,
  GetUniqueSalaryKeysCallbackType,
} from "./types";
import useBulkSalaryData from "./useBulkSalaryData";
import { applyTesSalaryTableCellToEmploymentAttributes } from "../../tesSalaryTables/tesSalaryTablesApi";

const BulkSalaryIncreasePage = () => {
  // const [submittedFilters, setSubmittedFilters] = useState<BulkSalaryIncreaseFilterValues | null>(null);

  const [isFilterMenuOpen, setFilterMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // FIXME: should menu be used?
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [menu, setMenu] = useState<ActionMenuState>(ACTION_MENU_DEFAULT_STATE);
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [unclearRows, setUnclearRows] = useState<string[]>([]);
  // const [bulkUpdateId, setBulkUpdateId] = useState<string | null>(null);
  const bulkUpdateId = useRef<string | null>(null);
  // const [shortUpdateId, setShortUpdateId] = useState<string | null>(null);
  const shortUpdateId = useRef<string | null>(null);
  const [pageLoadCount, setPageLoadCount] = useState<number>(0);
  const [loadComplete, setLoadComplete] = useState<boolean>(false);
  const [loadLimitReached, setLoadLimitReached] = useState<boolean>(false);
  // Track how many times each talent's salary has been processed during this page session
  //const [talentProcessCount, setTalentProcessCount] = useState<Map<string, number>>(new Map());
  const talentProcessCount = useRef<Map<string, number>>(new Map());
  // const [sortKey, setSortKey] = useState<string | null>(null);
  // const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // state for re-rendering on processing state change
  const [processingState, setProcessingState] =
    useState<BulkSalaryIncreaseProcessingState>({
      isProcessing: false,
      isCanceled: false,
      isCompleted: false,
      isPreviewing: false,
    });

  // state ref for managing state in async processing
  const processingRef = useRef({
    isProcessing: false,
    isCanceled: false,
    isCompleted: false,
  });

  const { t } = useTranslation();

  const filterMethods = useForm<BulkSalaryIncreaseFilterValues>({
    defaultValues: formatFilterDefaultValues(),
    mode: "onChange",
    shouldUnregister: true,
  });

  const { sendMessage } = useMessage();

  // Track previous target to detect changes
  const prevTarget = useRef<string>(filterMethods.getValues("target"));

  const {
    methods,
    fields,
    setData,
    setKtaData,
    updateRow,
    updateRowSuccess,
    getHeaders,
    getUniqueSalaryKeys,
    getUniqueKtaDates,
    toggleExpandedRow,
    updateRowClarity,
  } = useBulkSalaryData({ filterValues: filterMethods.watch() });

  // TODO make this clearer..
  const loadingLabel = loadComplete ?
    (loadLimitReached ? `...limited to ${MAX_TO_LOAD} items` :
      (processingRef.current.isProcessing ? `...processing ${fields.length} items` : `load complete with ${pageLoadCount} items`)
    ) : `...Loading in progress, loaded ${pageLoadCount} items so far`;

  // Invalidate data only when target changes
  useEffect(() => {
    const currentTarget = filterMethods.watch("target");
    if (prevTarget.current !== currentTarget) {
      // console.log(
      //   "target changed (cur, prev):",
      //   currentTarget,
      //   prevTarget.current,
      // );
      // Clear data in methods when target changes
      methods.reset({ data: [] });
      prevTarget.current = currentTarget;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMethods.watch("target")]);

  // TODO: show confirmation dialog?
  const cancelProcessing = () => {
    processingRef.current.isCanceled = true;
    processingRef.current.isProcessing = false;
    processingRef.current.isCompleted = false;
    setProcessingState((prev) => ({ ...prev, isCanceled: true }));
  };

  const startProcessing = () => {
    processingRef.current.isProcessing = true;
    setProcessingState((prev) => ({
      ...prev,
      isProcessing: true,
    }));
  };

  const completeProcessing = () => {
    processingRef.current.isCompleted = true;
    processingRef.current.isProcessing = false;
    setProcessingState((prev) => ({
      ...prev,
      isCompleted: true,
      isProcessing: false,
    }));
  };

  const resetProcessingState = useCallback(() => {
    processingRef.current = {
      isProcessing: false,
      isCanceled: false,
      isCompleted: false,
    };
    setProcessingState({
      // keep previewing as true
      // to allow next increases without fetching again
      isPreviewing: true,
      isProcessing: false,
      isCanceled: false,
      isCompleted: false,
    });
  }, []);

  useEffect(() => {
    if (processingState.isCompleted) {
      resetProcessingState();
      sendMessage("Korotukset käsitelty", "success");
      setCurrentProcessingIndex(0);
      setSelectedRowIds(() => []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    processingState,
    resetProcessingState,
    selectedRowIds,
    currentProcessingIndex,
    setSelectedRowIds,
    setCurrentProcessingIndex,
  ]);

  const dispatch = useAppDispatch();

  // Use filter value for row type detection
  const rowType = (() => {
    const target = prevTarget.current;
    if (target === BULK_SALARY_INCREASE_TARGETS.KTA) return "kta";
    if (target === BULK_SALARY_INCREASE_TARGETS.TES_TABLES)
      return "tes_tables";
    if (
      target === BULK_SALARY_INCREASE_TARGETS.EMPLOYMENT ||
      target === BULK_SALARY_INCREASE_TARGETS.EMPLOYMENT_SHIFTS
    )
      return "employment";
    return "shift_salaries";
  })();

  // const sortedFields = useMemo(() => {
  //   if (!sortKey) return fields;
  //   return [...fields].sort((a, b) => {
  //     const aValue = a[sortKey];
  //     const bValue = b[sortKey];
  //     if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
  //     if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
  //     return 0;
  //   });
  // }, [fields, sortKey, sortDirection]);

  const handleUnclearToggle = useCallback((
    rowId: MassUpdateStatusRowId,
    row: SalaryTableRow,
  ) => {
    const realrowId = resolveMassUpdateRowId(rowId, row);
    const addUnclear = !unclearRows.includes(realrowId);
    // const r = keyof
    const clarity = addUnclear
      ? MassUpdateRowClarity.UNCLEAR
      : MassUpdateRowClarity.NORMAL;
    let rrow: Record<string, MassUpdateRowClarity> = {
      [realrowId]: clarity,
    };
    if (isGroupedEmploymentSalaryRow(row) && !row.expanded) {
      // console.log("groupedEmploymentSalaryRow", row);
      row.groupedEmploymentRows.forEach((er) => {
        rrow[uiRowEmpToId(er)] = clarity;
      });
    }
    // console.log(
    //   "toggling unclear for",
    //   rowId,
    //   realrowId,
    //   rrow,
    //   addUnclear,
    //   row,
    //   shortUpdateId,
    // );
    dispatch(
      setMassUpdateState({
        massUpdateId: shortUpdateId.current!,
        rowClarities: rrow,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        // console.log("set mass update state", action);
        // TODO should perhaps check if we got the event back correctly
        const rowIds = Object.keys(rrow);
        // console.log("rowIds to update:", rowIds);
        if (addUnclear) {
          // if (action.payload)
          setUnclearRows(unclearRows.concat(rowIds));
        } else {
          const newUnclear = unclearRows.filter((r) => !rowIds.includes(r));
          setUnclearRows(newUnclear);
        }
        updateRowClarity(realrowId, addUnclear);
      } else {
        // console.warn("Failed to set mass update state", action);
        sendMessage("Rivin tilan asettaminen epäonnistui", "error");
      }
    });
  }, [dispatch, unclearRows, updateRowClarity, sendMessage]);

  // console.log("page fields", fields.length);
  const raiseDateSalaryKeys = useMemo(
    () => getUniqueSalaryKeys(GetUniqueSalaryKeysCallbackType.RAISE_DATE_SALARY),
    [getUniqueSalaryKeys],
  );
  const afterRaiseDateSalaryPerDateKeys = useMemo(
    () => getUniqueSalaryKeys(GetUniqueSalaryKeysCallbackType.AFTER_RAISE_SALARY_PER_DATE),
    [getUniqueSalaryKeys],
  );
  const uniqueKtaDates = useMemo(() => getUniqueKtaDates(), [getUniqueKtaDates]);

  const rows = useMemo(
    () =>
      processingState.isPreviewing
        ? fields.map((row, index) => {
            const rowKey = resolveMassUpdateRowId(row.id, row);
            return (
              <BulkSalaryIncreaseRowComponents
                key={rowKey}
                id={row.id}
                rowType={rowType}
                row={row}
                index={index}
                setMenu={setMenu}
                raiseDateSalaryKeys={raiseDateSalaryKeys}
                afterRaiseDateSalaryPerDateKeys={afterRaiseDateSalaryPerDateKeys}
                uniqueKtaDates={uniqueKtaDates}
                toggleUnclear={(rowId: MassUpdateStatusRowId) => {
                  handleUnclearToggle(rowId, row);
                }}
                toggleExpandedRow={(rowId: MassUpdateStatusRowId) => {
                  if (!isGroupedEmploymentSalaryRow(row)) {
                    return;
                  }
                  toggleExpandedRow(row);
                }}
              />
            );
          })
        : [],
    [
      processingState.isPreviewing,
      fields,
      rowType,
      setMenu,
      raiseDateSalaryKeys,
      afterRaiseDateSalaryPerDateKeys,
      uniqueKtaDates,
      handleUnclearToggle,
      toggleExpandedRow,
    ],
  );

  const handleFilterMenuOpenClose = () => {
    setFilterMenuOpen((prev) => !prev);
  };

  const startFetch = (filters: BulkSalaryIncreaseFilterValues) => {
    const query = formatToQuery(filters, null);
    const updateId = queryToShortId(query);
    dispatch(fetchMassUpdateState({ massUpdateId: updateId })).then(
      (action) => {
        if (isFulfilled(action)) {
          const status = action.payload;
          // console.log("mass update status", status);
          shortUpdateId.current = updateId;
          if (status.unclearRows.length > 0) {
            sendMessage(
              `Huomioitavaa: ${status.unclearRows.length} riviä jäi epäselviksi.`,
              "info",
            );
          }
          setUnclearRows(() => {
            // console.log("setting unclear rows", status.unclearRows);
            return status.unclearRows;
          });
          fetchData(filters, new Set(status.unclearRows));
        } else {
          // console.warn("Failed to fetch mass update status", action);
          fetchData(filters);
        }
      },
    );
  };

  const fetchData = (
    filters: BulkSalaryIncreaseFilterValues,
    unclearRows: Set<string> = new Set<string>(),
    currentCount: number = 0,
    cursor: string | null = null,
  ) => {
    // TODO if the query changes, see if you need to change queryToId
    if (!cursor) {
      talentProcessCount.current = new Map();
      setPageLoadCount(() => 0);
      setIsLoading(() => true);
      setLoadLimitReached(() => false);
      setLoadComplete(() => false);
    }
    const query = formatToQuery(filters, cursor);
    dispatch(fetchSalaries(query)).then((action) => {
      if (isFulfilled(action)) {
        // setSubmittedFilters(filters);
        const updateId = queryToId(query);
        // only shift salaries are paged
        let final =
          filters.target !== BULK_SALARY_INCREASE_TARGETS.SHIFT_SALARIES &&
          filters.target !== BULK_SALARY_INCREASE_TARGETS.TES_TABLES;
        // setBulkUpdateId((prev) => updateId);
        bulkUpdateId.current = updateId;
        if (filters.target !== BULK_SALARY_INCREASE_TARGETS.KTA) {
          if (
            filters.target === BULK_SALARY_INCREASE_TARGETS.SHIFT_SALARIES ||
            filters.target === BULK_SALARY_INCREASE_TARGETS.TES_TABLES
          ) {
            const pl = action.payload as ShiftSalaryData;
            if (pl.cursor) {
              //const newCount = ();
              const currentSize = currentCount + pl.data.length;
              if (currentSize < MAX_TO_LOAD - SHIFT_SALARY_PAGE_SIZE) {

              // console.log(`SHIFT cursor ${new Date().toISOString()}`, pl.cursor);
              // setCursor(pl.cursor);
                console.log("loading more", currentSize, pl.data.length)

                fetchData(filters, unclearRows, currentSize, pl.cursor);
                // increment page load counter
                setPageLoadCount((prev) => prev + pl.data.length);
              } else {
                console.log("Data limit reached, not loading more")
                setLoadLimitReached(() =>true);
                setPageLoadCount((prev) => prev +  pl.data.length);
                final = true;
              }
            } else {
              if (cursor || pl.data.length < SHIFT_SALARY_PAGE_SIZE) {
                // console.log("last round");
                final = true;
              }
            }
            const append = !!cursor;
            console.log(`Setting data ${pl.data.length} of ${pl.data[pl.data.length - 1]}, append: ${append}, unclearRows: ${unclearRows.size}`);
            setData(pl.data, filters, unclearRows, append);
          } else {
            setData(action.payload, filters, unclearRows);
          }
        } else {
          // console.log("KTA rows", action.payload);
          setKtaData(action.payload as unknown as Kta[], filters).then(() => {
            // console.log("KTA rows set");
          });
        }
        if (final) {
          // console.log("final", final);
          setLoadComplete(true);
          setCurrentProcessingIndex(0);
          setSelectedRowIds(() => []);
          // console.log("setting processing state");
          setProcessingState((prev) => ({ ...prev, isPreviewing: true }));
          // console.log("processing state set");
          setIsLoading(false);
        }
      }
    });
  };

  async function processBulkSalaryIncreases(): Promise<void> {
    const filterValues = filterMethods.getValues();

    startProcessing();

    const rowsToProcess = fields.filter((row) =>
      selectedRowIds.includes(row.id),
    );
    // let previousTalentId = "";
    // let sameTalentCount = 0;

    for (const [index, row] of rowsToProcess.entries()) {
      setCurrentProcessingIndex(currentProcessingIndex + 1);

      if (processingRef.current.isCanceled) {
        resetProcessingState();
        return;
      }

      try {
        if (prevTarget.current === BULK_SALARY_INCREASE_TARGETS.SHIFT_SALARIES) {
          // TODO I believe this wont work unless they are sorted by talent.. maybe they are?
          const currentCount = talentProcessCount.current.get(row.talentId) ?? 0;
          const commandRow = {
            ...row,
            version: row.version + currentCount,
          } as ShiftSalaryRow;

          const response = await setEmpAttrSalary(
            formatShiftSalaryToCommand(
              commandRow,
              filterValues,
              bulkUpdateId.current!,
            ),
            { requestId: nanoid() },
          );
          if (response) {
            talentProcessCount.current.set(row.talentId, currentCount + 1);

            // TODO take version from response for the talent
            updateRow(row, "status", SalaryIncreaseStatus.SUCCESS);
            // Below messes up status update, wouldn need to combine somehow, maybe
            // const realrowId = uiRowToId(row);
            // if (unclearRows.includes(realrowId)) {
            //   handleUnclearToggle(realrowId, row);
            // } else {
            //   console.log("not unclear", realrowId, unclearRows)
            // }
          }
        } else if (prevTarget.current === BULK_SALARY_INCREASE_TARGETS.TES_TABLES) {
          const currentCount = talentProcessCount.current.get(row.talentId) ?? 0;
          const commandRow = {
            ...row,
            version: row.version + currentCount,
          } as ShiftSalaryRow;

          const response = await applyTesSalaryTableCellToEmploymentAttributes(
            formatTesTablesShiftSalaryToCommand(
              commandRow,
              filterValues,
              bulkUpdateId.current!,
            ),
            { requestId: nanoid() },
          );
          if (response) {
            talentProcessCount.current.set(row.talentId, currentCount + 1);
            updateRow(row, "status", SalaryIncreaseStatus.SUCCESS);
          }
        } else {
          if (isGroupedEmploymentSalaryRow(row) && !row.expanded) {
            let responseCount = 0;
            const newSalary = filterFalsyParameters(formatSalaryIncreaseValues(row.salaryIncrease));
            for (const row2 of row.groupedEmploymentRows) {
              const response = await editEmpSalary(
                formatEmploymentSalaryToCommand(
                  row2,
                  filterValues,
                  bulkUpdateId.current!,
                  newSalary,
                ),
                {
                  requestId: nanoid(),
                },
              );

              if (response) {
                // this should wait for all?
                responseCount++;
                setCurrentProcessingIndex(index + 1 + responseCount);
                // console.log("salary update response", response);
                // if (row.employmentId !== row2.employmentId) {
                //   // only update subrow if different from main row, main row is updated below
                //   updateGroupedRow(row, row2, "status", SalaryIncreaseStatus.SUCCESS);
                // }
              }
            }
            // TODO update subrows too? I think yes
            if (responseCount === row.groupedEmploymentRows.length) {
              updateRowSuccess(row);
            } else {
              console.error("failed to update all grouped rows", row, responseCount);
            }
          } else {
            if (isEmploymentSalaryTableRow(row)) {
              const response = await editEmpSalary(
                formatEmploymentSalaryToCommand(
                  row,
                  filterValues,
                  bulkUpdateId.current!,
                ),
                {
                  requestId: nanoid(),
                },
              );

              if (response) {
                updateRow(row, "status", SalaryIncreaseStatus.SUCCESS);
              }
            } else {
            if (isKtaRow(row)) {
              /*
            // example fetch result
  {
      "date": "2025-04-08",
      "rows": [
          {
              "keskiansiolaskenta": "{\"ongoingAverages\":{\"2025-07-01\":{\"valueDate\":\"2025-08-01\",\"salary\":\"115.2000000000000000\",\"workTime\":\"8.00000000\"}}}"
          }
      ]
  }
      // example add body

      {
      "isInternal": true,
      "date": "2025-04-08",
      "contEmpId": "x-fgOmsIGrCe7ofzW_3Jng",
      "employmentId": "qiuualWWJXV38-3Qg7j9xw",
      "values": {
          "keskiansiolaskenta": "{\"ongoingAverages\":{\"2025-07-01\":{\"valueDate\":\"2025-08-01\",\"salary\":\"129.6000000000000000\",\"workTime\":\"9.00000000\"}}}"
      }

      export interface AddAccumDelta {
        isInternal: boolean;
        contEmpId: string;
        employmentId: Nullable<string>;
        year?: string;
        month?: string;
        custOrgId?: string;
        values: { // WTF
          time: string; // TODO: fix, these are dynamic stateNames
          days: string;
        };
      }
      */

              /**
             * {
             "meta": {
             "keys": [],
             "stateNames": [
             "keskiansio"
             ]
             },
             "data": [
             {
             "date": "2025-05-01",
             "rows": [
             {
             "keskiansio": "14.40000000"
             }
             ]
             }


             {
             "isInternal": false,
             "date": "2025-05-27",
             "contEmpId": "TGPllw0AFNgTDBP6AFf3jg",
             "employmentId": "3QgOk3C3Uji5yMI_VbnFWg",
             "values": {
             "keskiansio": "15"
            }
             }
             }
             */
              // console.log("is kta row", row);

              const accumDataKeskiansio = formatKtaToCommand(
                row,
                filterValues,
                bulkUpdateId.current!,
              );
              if (accumDataKeskiansio) {
                dispatch(addMassAccumulationDelta(accumDataKeskiansio)).then(
                  (action) => {
                    if (isFulfilled(action)) {
                      // console.log("sent keskiansio data", action.payload);
                    } else {
                      console.error("failed keskiansio", action);
                      sendMessage(`Keskiansio virhe rivillä ${row.id}`, "error");
                      updateRow(row, "status", SalaryIncreaseStatus.FAILED);
                    }
                  },
                );
              }

              const accumData = formatKtaLaskentaToCommand(
                row,
                bulkUpdateId.current!,
              );
              if (accumData) {
                // console.log("accom", accumData);
                const accumString = JSON.stringify(accumData);
                // console.log("accumString", accumString);

                dispatch(addMassAccumulationDelta(accumData)).then((action) => {
                  if (isFulfilled(action)) {
                    // console.log("sent keskiansiolaskenta data", action.payload);
                    updateRow(row, "status", SalaryIncreaseStatus.SUCCESS);
                  } else {
                    console.error("failed keskiansiolaskenta", action);
                    sendMessage(
                      `Keskiansiolaskenta virhe rivillä ${row.id}`,
                      "error",
                    );
                    updateRow(row, "status", SalaryIncreaseStatus.FAILED);
                  }
                });
              }
            }
            else {
              // console.warn("Unknown row type, cannot process", row);
              sendMessage(`Tuntematon rivityyppi rivillä ${row.id}`, "error");
              updateRow(row, "status", SalaryIncreaseStatus.FAILED);
            }
          }
        }
        }
      } catch (error) {
        // TODO tämä on hämäävä virhe
        sendMessage(`Virhe rivillä ${row.id}: ${error}`, "error");
        updateRow(row, "status", SalaryIncreaseStatus.FAILED);
      }
      // TODO: delay is needed only with employments
      // wait for 2 seconds for backend processing before continuing to next row
      await delay(2000);
    }

    completeProcessing();
  }

  const updateSelectedRowIds = (ids: string[]) => setSelectedRowIds(ids);

  const countSelected = () => {
    // console.log("countSelected selected", selectedRowIds);
    if (selectedRowIds.length === 0) {
      // console.log("countSelected zero");
      return 0;
    }
    const selected = fields.filter((f) => {
      const id = f.id;
      const hasIt3 = selectedRowIds.filter((s) => s === id).length > 0;
      // console.log("evaluating ", f.id, hasIt, hasIt2, hasIt3, hasIt4);
      return hasIt3;
    });
    if (isGroupedEmploymentSalaryRow(selected[0]) && !selected[0].expanded) {
      let count = 0;
      selected.forEach((s) => {
        if (isGroupedEmploymentSalaryRow(s)) {
          count += s.groupedEmploymentRows.length;
        } else {
          count += 1;
        }
      });
      // console.log("countSelected grouped count ", selectedRowIds.length);
      return count;
    }
    // console.log("countSelected count ", selectedRowIds.length);
    return selectedRowIds.length;
  };

  // console.log("going to return, is loading", isLoading);
  return (
    <Grid container rowGap={3} sx={{ position: "relative" }}>
      <BulksSalaryIncreaseHeader
        buttonDisabled={!processingState.isPreviewing}
        onClick={processBulkSalaryIncreases}
        cancelProcessing={cancelProcessing}
        showCancelButton={processingState.isProcessing}
      />
      <FilterSearchField
        width="1208px"
        isOpen={isFilterMenuOpen}
        toggleMenu={handleFilterMenuOpenClose}
        label={t("searchAndFilter")}
        value=""
        searchHistory={false}
        hideSearchHistory
        translation="talentProfile"
        filters={[]}
        updateFilters={() => {}}
        isLoading={isLoading}
        fieldComponent={<BulkSalaryIncreaseFiltersBar />}
        showFilterComponents={false}
        menuInFlow
      >
        <FormProvider {...filterMethods}>
          <BulkSalaryIncreaseFilters
            fetchData={startFetch}
            handleMenuToggle={handleFilterMenuOpenClose}
          />
        </FormProvider>
      </FilterSearchField>
      <BulkSalaryIncreaseProgressBar
        total={countSelected()}
        currentProcessingIndex={currentProcessingIndex}
        processingState={processingState}
      />
      {(isLoading || loadLimitReached || pageLoadCount == 0) && (
        <>
          <Typography variant="body2" component="div">
            {loadingLabel}
          </Typography>
        </>
      )}
      <FormProvider {...methods}>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          !isEmpty(rows) && (
            <BulkSalaryIncreaseTable
              headers={getHeaders()}
              rows={rows}
              selectedIds={selectedRowIds}
              updateSelectedIds={updateSelectedRowIds}
            />
          )
        )}
      </FormProvider>
    </Grid>
  );
};

export default BulkSalaryIncreasePage;
